import React from "react";
import { generatePath, useNavigate } from "react-router-dom";

import I40Facebook from "assets/icons/I40Facebook";
import I40Instagram from "assets/icons/I40Instagram";
import IGoogleStore from "assets/icons/IGoogleStore";
import IAppStore from "assets/icons/IAppStore";
import I200TextLogo from "assets/icons/I200TextLogo";

import { Footer } from "components/Layout";

import ROUTES from "navigation/routes";

import { LINKS } from "constants/links";
import { SUPPORT_EMAIL } from "constants/support";

import {
  BottomContentContainer,
  BottomContent,
  BottomTextWrapper,
  CenterContainer,
  CenterColumn,
  Gap,
  Icons,
  IconWrapper,
  Link,
  Logo,
  Menu,
  SideContainer,
  Text,
  Title,
} from "./styles";
import {
  WithTranslation,
  withTranslation,
  useTranslation,
} from "react-i18next";

type Props = {
  disabled?: boolean;
} & WithTranslation;

const MAIN_MENU_ITEMS = [
  { titleKey: "menu.workouts", path: ROUTES.WORKOUT },
  { titleKey: "menu.favorites", path: ROUTES.WORKOUT_FAVORITES },
  { titleKey: "menu.calendar", path: ROUTES.CALENDAR },
];

function capitalize(word: string): string {
  const capitalizedWord = word.trim();
  if (!capitalizedWord) return "";
  return (
    capitalizedWord.charAt(0).toUpperCase() +
    capitalizedWord.slice(1).toLowerCase()
  );
}

const AppFooter: React.FC<Props> = props => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const openExternalLink = (link: string) => () => {
    window.open(link);
  };

  const openInternalLink = (path: string) => () => {
    navigate(generatePath(path));
  };

  const renderLeft = () => (
    <SideContainer left>
      <Logo>
        <I200TextLogo />
      </Logo>
      <Text>{t("footer.faster-text")}</Text>
      <Icons left>
        <IconWrapper onClick={openExternalLink(LINKS.FACEBOOK)}>
          <I40Facebook />
        </IconWrapper>
        <IconWrapper onClick={openExternalLink(LINKS.INSTAGRAM)}>
          <I40Instagram />
        </IconWrapper>
      </Icons>
    </SideContainer>
  );

  const renderCenter = () => (
    <CenterContainer>
      <CenterColumn>
        <Title>{t("labels.main-menu")}</Title>
        {MAIN_MENU_ITEMS.map(item => (
          <Menu key={`footer-${item.titleKey}`}>
            <Link onClick={openInternalLink(item.path)}>
              {capitalize(t(item.titleKey))}
            </Link>
          </Menu>
        ))}
      </CenterColumn>
    </CenterContainer>
  );

  const renderRight = () => (
    <SideContainer right>
      <Title>{t("actions.download-app")}</Title>
      <Text>{t("footer.download-app")}</Text>
      <Icons>
        <IconWrapper
          onClick={openExternalLink(LINKS.GOOGLE_PLAY_LINK)}
          storeIcon
        >
          <IGoogleStore />
        </IconWrapper>
        <IconWrapper onClick={openExternalLink(LINKS.APP_STORE_LINK)} storeIcon>
          <IAppStore />
        </IconWrapper>
      </Icons>
    </SideContainer>
  );

  const renderBottom = () => (
    <BottomContentContainer>
      <BottomContent>
        <BottomTextWrapper>
          <Text small>© FasterWay. {t("all-rights-reserved")} </Text>
        </BottomTextWrapper>
      </BottomContent>
      <BottomContent>
        <BottomTextWrapper>
          <Link small href={`mailto:${SUPPORT_EMAIL}`}>
            {t("support")}
          </Link>
        </BottomTextWrapper>
        <BottomTextWrapper>
          <Link small onClick={openExternalLink(LINKS.PRIVACY_POLICY)}>
            {t("privacy-policy")}
          </Link>
        </BottomTextWrapper>
        <BottomTextWrapper>
          <Link small onClick={openExternalLink(LINKS.TERMS_AND_CONDITIONS)}>
            {t("terms-conditions")}
          </Link>
        </BottomTextWrapper>
      </BottomContent>
      <BottomContent>
        <Gap />
      </BottomContent>
    </BottomContentContainer>
  );
  const { disabled } = props;

  return (
    <Footer
      centerContent={renderCenter()}
      leftContent={renderLeft()}
      rightContent={renderRight()}
      bottomContent={renderBottom()}
      disabled={disabled}
    />
  );
};

export default withTranslation()(AppFooter);
